"use client";

/**
 * Checkout Theme Provider
 *
 * Provides theming context and CSS variable injection for checkout components.
 * Supports preset themes (light/dark) with optional custom overrides.
 */

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import type {
  CheckoutTheme,
  CheckoutThemeProviderProps,
  PartialCheckoutTheme,
} from "./types.ts";
import { lightTheme, darkTheme, createCustomTheme } from "./presets.ts";
import { overridesToCssVars, cssVarsToStyle } from "./css-vars.ts";

/**
 * Context for accessing the current checkout theme
 */
type CheckoutThemeContextValue = {
  theme: CheckoutTheme;
  preset: "light" | "dark";
  setPreset: (preset: "light" | "dark") => void;
  setCustomTheme: (overrides: PartialCheckoutTheme) => void;
};

const CheckoutThemeContext =
  React.createContext<CheckoutThemeContextValue | null>(null);

/**
 * Hook to access the checkout theme context
 *
 * @throws Error if used outside of CheckoutThemeProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, preset, setPreset } = useCheckoutTheme();
 *   return (
 *     <button onClick={() => setPreset('dark')}>
 *       Switch to Dark Mode
 *     </button>
 *   );
 * }
 * ```
 */
export function useCheckoutTheme(): CheckoutThemeContextValue {
  const context = React.useContext(CheckoutThemeContext);
  if (!context) {
    throw new Error(
      "useCheckoutTheme must be used within a CheckoutThemeProvider",
    );
  }
  return context;
}

/**
 * Optional hook that returns null if used outside provider
 * Useful for components that can work with or without theming
 */
export function useCheckoutThemeOptional(): CheckoutThemeContextValue | null {
  return React.useContext(CheckoutThemeContext);
}

/**
 * Checkout Theme Provider Component
 *
 * Wraps checkout components and injects CSS variables for theming.
 *
 * @example
 * ```tsx
 * // Basic usage with preset
 * <CheckoutThemeProvider preset="light">
 *   <CheckoutRegular {...props} />
 * </CheckoutThemeProvider>
 *
 * // Custom theme overrides
 * <CheckoutThemeProvider
 *   preset="light"
 *   theme={{
 *     colors: { primary: '#FF6B00' },
 *     typography: { fontFamily: 'Inter, sans-serif' },
 *   }}
 * >
 *   <CheckoutRegular {...props} />
 * </CheckoutThemeProvider>
 * ```
 */
export function CheckoutThemeProvider({
  preset: initialPreset = "light",
  theme: customOverrides,
  children,
  className,
}: CheckoutThemeProviderProps) {
  const [preset, setPreset] = React.useState<"light" | "dark">(initialPreset);
  const [customTheme, setCustomThemeState] = React.useState<
    PartialCheckoutTheme | undefined
  >(customOverrides);

  // Compute the final theme by merging preset with custom overrides
  const theme = React.useMemo(() => {
    const baseTheme = preset === "dark" ? darkTheme : lightTheme;
    if (customTheme) {
      return createCustomTheme(preset, customTheme);
    }
    return baseTheme;
  }, [preset, customTheme]);

  // Inline only what the caller overrode. The full theme used to go inline
  // here, which beat the variables a page sets on a web component's host
  // element (Website v2 themes the checkout that way): the defaults now stay
  // with the stylesheet (:host in the components, :root elsewhere).
  const cssVars = React.useMemo(() => overridesToCssVars(customTheme), [customTheme]);
  const style = React.useMemo(() => cssVarsToStyle(cssVars), [cssVars]);

  // Update custom theme
  const setCustomTheme = React.useCallback(
    (overrides: PartialCheckoutTheme) => {
      setCustomThemeState(overrides);
    },
    [],
  );

  // Context value
  const contextValue = React.useMemo<CheckoutThemeContextValue>(
    () => ({
      theme,
      preset,
      setPreset,
      setCustomTheme,
    }),
    [theme, preset, setCustomTheme],
  );

  return (
    <CheckoutThemeContext.Provider value={contextValue}>
      <div
        className={cn("checkout-theme-root", className)}
        style={style}
        data-checkout-theme={preset}
      >
        {children}
      </div>
    </CheckoutThemeContext.Provider>
  );
}

/**
 * Higher-order component to wrap a component with theme provider
 * Useful for wrapping checkout flows that need theming
 */
export function withCheckoutTheme<P extends object>(
  Component: React.ComponentType<P>,
  defaultProps?: Partial<CheckoutThemeProviderProps>,
) {
  const WrappedComponent = React.forwardRef<
    unknown,
    P & Partial<CheckoutThemeProviderProps>
  >((props, ref) => {
    const { preset, theme, ...componentProps } = props;
    return (
      <CheckoutThemeProvider
        preset={preset ?? defaultProps?.preset}
        theme={theme ?? defaultProps?.theme}
      >
        <Component {...(componentProps as P)} ref={ref} />
      </CheckoutThemeProvider>
    );
  });
  WrappedComponent.displayName = `withCheckoutTheme(${Component.displayName || Component.name})`;
  return WrappedComponent;
}
